import React, { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useSafeTranslation } from '@/hooks/useSafeTranslation';

import { useAuthStore } from '@/stores/authStore';
import { useRestaurantStore } from '@/stores/restaurantStore';
import { Button } from '@/components/ui/Button';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { MobilePartnershipFooter } from '@/components/common/PartnershipBranding';
import { NotificationPanel } from '@/components/common/NotificationPanel';
import { Sidebar, getNavigationItems } from './Sidebar';
import { NavMenu, NavSearch } from '@/assets/nav-icons';


// Header Component
const Header = ({ onMenuClick }: { onMenuClick: () => void }) => {
  const { t } = useSafeTranslation();
  const { user } = useAuthStore();
  const { getDisplayName } = useRestaurantStore();
  const { pathname } = useLocation();

  // Reuse the sidebar's own nav config so the header cannot drift from the
  // labels beside it. Longest match wins, so /manager/tables beats /.
  const pageTitle = React.useMemo(() => {
    const match = getNavigationItems(t)
      .filter((item) => pathname === item.href || pathname.startsWith(`${item.href}/`))
      .sort((a, b) => b.href.length - a.href.length)[0];
    return match?.label ?? '';
  }, [pathname, t]);

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-neutral-200 bg-white/90 backdrop-blur-md px-4 sm:px-6">
      {/*
        Mobile Menu Button & Restaurant Branding.

        min-w-0 is load-bearing: flex items default to `min-width: auto`, so
        without it the restaurant name refuses to shrink below its intrinsic
        width and pushes the whole header past the viewport. On a 390px screen
        that overflow was 9px — enough to make every page scroll sideways.
      */}
      <div className="flex min-w-0 items-center space-x-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={onMenuClick}
          className="lg:hidden"
        >
          <NavMenu className="h-5 w-5" />
        </Button>
        
        {/*
          Page context, not branding. The Nova logo already sits in the sidebar
          header immediately to the left; rendering it again here put the same
          mark twice in the top-left corner of every screen.
        */}
        <div className="hidden lg:flex min-w-0 items-center">
          <h1 className="truncate text-base font-semibold text-neutral-900" data-testid="page-title">
            {pageTitle}
          </h1>
        </div>

        {/* Mobile Platform/Restaurant Name */}
        <div className="min-w-0 lg:hidden">
          <h1 className="truncate text-lg font-semibold text-neutral-900">
            {user?.role === 'super_admin' ? t('brand.platformTitle') : getDisplayName()}
          </h1>
        </div>
      </div>

      {/*
        Search Bar — hidden on phones. At 390px it cost ~180px of a 358px
        content box while the sidebar's own search remains a tap away, and its
        intrinsic min-width was a second source of header overflow.
      */}
      <div className="mx-4 hidden max-w-lg flex-1 md:block">
        <div className="relative">
          <NavSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          <input
            type="text"
            placeholder={t('common.search')}
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-transparent bg-neutral-100 text-sm placeholder-neutral-400 focus:bg-white focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-colors duration-150 min-h-10"
          />
        </div>
      </div>

      {/*
        Right Side Actions — never shrinks; the branding gives way instead.

        No avatar or user name here. The sidebar footer already renders the
        signed-in identity with more context (avatar, name, role, restaurant),
        so a second copy in the top-right corner said the same thing twice on
        every screen while the header's job is page context.
      */}
      <div className="flex shrink-0 items-center space-x-3">
        {/* Language Switcher */}
        <LanguageSwitcher variant="header" />

        {/* Real-time Notifications */}
        <NotificationPanel />
      </div>
    </header>
  );
};

// Main Dashboard Layout Component
export const DashboardLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useAuthStore();

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Desktop Sidebar - Fixed Position */}
      <Sidebar variant="desktop" />
      
      {/* Mobile Sidebar - Overlay */}
      <Sidebar
        variant="mobile"
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/*
        Main Content - Offset for fixed sidebar.

        min-w-0 on the column and on <main> is what lets `overflow-x-auto`
        wrappers actually work. Flex items default to `min-width: auto`, so
        without it a wide table stretches this column to its own intrinsic
        width (1084px for the 8-column order table) instead of being clipped
        and scrolled inside its wrapper — which is how one wide table ended up
        dragging the entire page sideways.
      */}
      <div className="lg:ml-72 flex min-w-0 flex-col min-h-screen">
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <main className="min-w-0 flex-1">
          <div className="min-w-0 p-6 pb-20 lg:pb-6">
            <Outlet />
          </div>
        </main>
        
        {/* Mobile Partnership Footer - Hidden for Super Admin */}
        {user?.role !== 'super_admin' && <MobilePartnershipFooter />}
      </div>
    </div>
  );
};